import React from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_PURCHASE_ORDER,
  GET_ALL_PURCHASE_ORDERS,
  LINK_PURCHASE_ORDER_JOBS,
  REMOVE_PURCHASE_ORDER,
  UPDATE_PURCHASE_ORDER,
} from '../../../graph-queries/tar/purchaseOrder';
import { TPurchaseOrder } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import PurchaseOrderForm from '../../organisms/PurchaseOrderForm/PurchaseOrderForm';
import { ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import {
  PURCHASE_ORDER_STATUS_ENUM,
  PURCHASE_ORDER_TYPE_ENUM,
} from '../../../lib/constants/dbConstants';

export const columns: ColumnDef<TPurchaseOrder>[] = [
  {
    header: 'PO Number',
    accessorKey: 'poNumber',
  },
  {
    header: 'Type',
    accessorKey: 'type',
    accessorFn: (row) => PURCHASE_ORDER_TYPE_ENUM[row.type],
  },
  {
    header: 'Vendor',
    accessorKey: 'vendor',
  },
  {
    header: 'Status',
    accessorKey: 'status',
    accessorFn: (row) => PURCHASE_ORDER_STATUS_ENUM[row.status],
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const PurchaseOrdersPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const { data, loading } = useQuery(GET_ALL_PURCHASE_ORDERS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const [createPurchaseOrder, { loading: isCreating }] = useMutation(CREATE_PURCHASE_ORDER, {
    refetchQueries: [GET_ALL_PURCHASE_ORDERS],
    awaitRefetchQueries: true,
  });
  const [updatePurchaseOrder, { loading: isUpdating }] = useMutation(UPDATE_PURCHASE_ORDER, {
    refetchQueries: [GET_ALL_PURCHASE_ORDERS],
    awaitRefetchQueries: true,
  });
  const [linkPurchaseOrderJobs, { loading: isLinking }] = useMutation(LINK_PURCHASE_ORDER_JOBS, {
    refetchQueries: [GET_ALL_PURCHASE_ORDERS],
    awaitRefetchQueries: true,
  });
  const [removePurchaseOrder, { loading: isRemoving }] = useMutation(REMOVE_PURCHASE_ORDER, {
    refetchQueries: [GET_ALL_PURCHASE_ORDERS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (purchaseOrder: TPurchaseOrder) => {
    const { purchaseOrderId, updatedBy, updatedAt, tarJobs, ...input } = purchaseOrder;
    return createPurchaseOrder({ variables: { input } }).then(() =>
      setAlertText('Purchase order successfully added')
    );
  };

  const onEditSubmit = (purchaseOrder: TPurchaseOrder) => {
    const { updatedBy, updatedAt, tarJobs, ...input } = purchaseOrder;
    return updatePurchaseOrder({ variables: { input } }).then(() =>
      setAlertText('Purchase order successfully updated')
    );
  };

  const onLinkSubmit = (selection: TPurchaseOrder[], jobIds: number[]) => {
    return Promise.all(
      selection.map((purchaseOrder) =>
        linkPurchaseOrderJobs({
          variables: {
            purchaseOrderId: purchaseOrder.purchaseOrderId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Purchase Order successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TPurchaseOrder[]) => {
    return Promise.all(
      selection.map((purchaseOrder) =>
        removePurchaseOrder({
          variables: { purchaseOrderId: purchaseOrder.purchaseOrderId },
        })
      )
    );
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;

  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TPurchaseOrder>
            hideSelectionColumn
            aspectName="purchase order"
            data={(data?.allPurchaseOrders as unknown as TPurchaseOrder[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <PurchaseOrderForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <PurchaseOrderForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the purchase order tracker..."
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default PurchaseOrdersPage;
